import { Component, type ReactNode } from "react";

type Props = { children: ReactNode; resetKey?: string };
type State = { error: Error | null };

/**
 * Captura erros de renderização e falhas de carregamento de chunks lazy
 * (rede instável, deploy novo invalidando o hash do bundle, etc.) para
 * não deixar a tela em branco/azul. Em caso de chunk faltando, tenta
 * recarregar automaticamente uma vez por sessão; nos demais casos,
 * mostra uma mensagem com botão para tentar de novo.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    const msg = String(error?.message || "");
    const isChunkError =
      /Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
        msg,
      );
    if (isChunkError && typeof window !== "undefined") {
      const key = "lt-chunk-reload-attempt";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
    }
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  reset = () => {
    if (typeof window !== "undefined") {
      try { sessionStorage.removeItem("lt-chunk-reload-attempt"); } catch { /* ignore */ }
    }
    this.setState({ error: null });
  };

  reload = () => {
    if (typeof window !== "undefined") {
      try { sessionStorage.removeItem("lt-chunk-reload-attempt"); } catch { /* ignore */ }
      window.location.reload();
    }
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6">
        <div className="max-w-md text-center space-y-4">
          <h1 className="font-serif text-2xl text-foreground">Ops, algo deu errado</h1>
          <p className="text-sm text-muted-foreground">
            Não foi possível carregar esta tela. Suas dezenas escolhidas em Combinações e Bolões ficaram salvas — clique em recarregar para continuar.
          </p>
          <div className="flex justify-center gap-2">
            <button
              onClick={this.reload}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Recarregar
            </button>
            <button
              onClick={this.reset}
              className="rounded-md border border-lt-border px-4 py-2 text-sm font-medium text-foreground hover:bg-lt-bg2"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;